import rectangleOrange from "../../../assets/rectangleOrange.png";
import rectangleOrangeFilled from "../../../assets/rectangleOrangeFilled.png";
import { GetIndexInArray } from "../Centerpanel.tsx";
import { GeoJSONSource, Map } from "mapbox-gl";
import { FillDataForRouteLine } from "./Routeline.ts";
import { RouteState } from "../../../api/edits/editRoute.responses.ts";
import {
  MapBoxGLLineData,
  MapBoxGLPoint,
  MapBoxGLPointData,
} from "../../../ts/interfaces/mapbox.interfaces.ts";

// pre:   - route has the route with focused sounds
//        - map has mapbox object on which to display
// post:  - map with two layers for selected sound point and line
export function DisplaySelectedSound(map: Map, route: RouteState): void {
  // ading point selection
  map.on("load", () => {
    // Add an image to use as a custom marker
    map.loadImage(rectangleOrange, (error, image) => {
      if (error) throw error;
      map.addImage("rectangle", image as ImageBitmap);
      // Add a GeoJSON point source
      map.addSource("points_selection_source", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
      // Add a symbol layer
      map.addLayer({
        id: "points_selection",
        type: "symbol",
        source: "points_selection_source",
        layout: {
          "icon-image": "rectangle",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-optional": true,
          "text-field": ["get", "title"],
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0, 0],
          "text-anchor": "center",
        },
      });
    });
  });

  // adding line selection
  let data: number[][] = [];
  map.on("load", () => {
    map.addSource("line_selection_source", {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          // coordinates: FillDataForRouteLine(route),
          coordinates: data,
        },
      },
    });
    map.addLayer({
      id: "line_selection",
      type: "line",
      source: "line_selection_source",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#ff7917",
        "line-width": 5,
      },
    });
  });

  // ading line vertex selection
  map.on("load", () => {
    // Add an image to use as a custom marker
    map.loadImage(rectangleOrangeFilled, (error, image) => {
      if (error) throw error;
      map.addImage("rectangleFilled", image as ImageBitmap);
      // Add a GeoJSON point source
      map.addSource("line_vertex_selection_source", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
      // Add a symbol layer
      map.addLayer({
        id: "line_vertex_selection",
        type: "symbol",
        source: "line_vertex_selection_source",
        layout: {
          "icon-image": "rectangleFilled",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-optional": true,
          "text-field": ["get", "title"],
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0, 0],
          "text-anchor": "center",
        },
      });
    });
  });
}

// pre:   - route has the route with previous selected sound
//        - stateSelected has the name of the new selected sound
// post:  returns array data of new selected sound
export function FillDataForUpdateSelectionPoint(
  route: RouteState,
  stateSelected: string
): MapBoxGLPointData {
  let data: MapBoxGLPointData = {
    type: "FeatureCollection",
    features: [],
  };
  if (
    stateSelected.substring(0, 5) !== "rprop" &&
    stateSelected !== "bss" &&
    stateSelected !== "fss" &&
    stateSelected !== "pois"
  ) {
    // detect layer
    if (stateSelected.substring(0, 3) === "bss") {
      let str: string = stateSelected.substring(3);
      let i: number = GetIndexInArray(Number(str), route.backgroundSounds);
      let point: MapBoxGLPoint = {
        type: "Feature",
        geometry: {
          type: "Point",
          // add geometry from route object
          coordinates: [
            route.backgroundSounds[i].longitude,
            route.backgroundSounds[i].latitude,
          ],
        },
        properties: {
          // add name
          title: "",
        },
      };
      data.features.push(point);
    } else if (stateSelected.substring(0, 3) === "fss") {
      let str: string = stateSelected.substring(3);
      let i: number = GetIndexInArray(Number(str), route.focusedSounds);
      let point: MapBoxGLPoint = {
        type: "Feature",
        geometry: {
          type: "Point",
          // add geometry from route object
          coordinates: [
            route.focusedSounds[i].longitude,
            route.focusedSounds[i].latitude,
          ],
        },
        properties: {
          // add name
          title: "",
        },
      };
      data.features.push(point);
    } else if (stateSelected.substring(0, 4) === "pois") {
      let str: string = stateSelected.substring(4);
      let i: number = GetIndexInArray(Number(str), route.pointsOfInterest);
      let point: MapBoxGLPoint = {
        type: "Feature",
        geometry: {
          type: "Point",
          // add geometry from route object
          coordinates: [
            route.pointsOfInterest[i].longitude,
            route.pointsOfInterest[i].latitude,
          ],
        },
        properties: {
          // add name
          title: "",
        },
      };
      data.features.push(point);
    }
  }
  return data;
}

// pre:   - route has the route with previous selected vertex
//        - stateSelected has the name of the new selected
// post:  returns array data of new selected vertex
export function FillDataForUpdateSelectionLineVertex(
  route: RouteState,
  stateSelected: string
): MapBoxGLPointData {
  let data: MapBoxGLPointData = {
    type: "FeatureCollection",
    features: [],
  };

  // detect vertex
  if (stateSelected.substring(0, 5) === "rline" && stateSelected.length > 5) {
    let str: string = stateSelected.substring(5);
    let i: number = Number(str);
    let point: MapBoxGLPoint = {
      type: "Feature",
      geometry: {
        type: "Point",
        // add geometry from route object
        coordinates: [
          route.linePoints[i].longitude,
          route.linePoints[i].latitude,
        ],
      },
      properties: {
        // add name
        title: "",
      },
    };
    data.features.push(point);
  }

  return data;
}

// pre:   - route has the route with previous selected route line
//        - stateSelected has the name of the new selected route line
// post:  returns array data of new selected route line
export function FillDataForUpdateSelectionLine(
  route: RouteState,
  stateSelected: string
): MapBoxGLLineData {
  let coords: number[][] = [];

  if (stateSelected === "rline" || stateSelected.substring(0, 5) === "rline") {
    coords = FillDataForRouteLine(route);
  }
  let data: MapBoxGLLineData = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "LineString",
      coordinates: coords,
    },
  };

  return data;
}
// pre:   - route has the route with  sounds
//        - map has mapbox object on which to display
//        - stateSelected has the name of the selected entry from treepanel
// post:  - map layer selected has the displayed selected sound
export function UpdateSelectedSound(
  map: Map,
  route: RouteState,
  stateSelected: string
): void {
  if (map != null) {
    // route_line
    let layLine: any = map.getSource("line_selection_source") as GeoJSONSource;
    if (layLine !== undefined) {
      layLine.setData(FillDataForUpdateSelectionLine(route, stateSelected));
    }
    // line vertices
    let layVertex: any = map.getSource(
      "line_vertex_selection_source"
    ) as GeoJSONSource;
    if (layVertex !== undefined) {
      layVertex.setData(
        FillDataForUpdateSelectionLineVertex(route, stateSelected)
      );
    }
    // sound points
    let laySound: any = map.getSource(
      "points_selection_source"
    ) as GeoJSONSource;
    if (laySound !== undefined) {
      laySound.setData(FillDataForUpdateSelectionPoint(route, stateSelected));
    }
  }
}
